import { InputLabel, Tooltip, styled, Box, SxProps, Theme, Typography } from '@mui/material';
import { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { d_flex } from '../../styles/common';
import { BsInfoCircle } from 'react-icons/bs';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { modalModifyBillData, ModalModifyBillDataType } from '../../store/recoil/modalModifyBill';

interface InputTitleProps {
  title: string;
  icon?: ReactNode;
  sx?: SxProps<Theme>;
  tooltip?: {
    text: ReactElement;
    direction:
      | 'bottom'
      | 'left'
      | 'right'
      | 'top'
      | 'bottom-end'
      | 'bottom-start'
      | 'left-end'
      | 'left-start'
      | 'right-end'
      | 'right-start'
      | 'top-end'
      | 'top-start'
      | undefined;
  };
  required?: boolean;
  visibleButton?: boolean;
  classification?: string;
  onClickButtonName?: (type: string) => void;
  onClickButtonTime?: (type: string) => void;
  bold?: boolean;
  openInfo?: { type: string; open: boolean };
  smsInfo?: string;
}

const StyledInputLabel = styled(InputLabel)({
  width: '100%',
  height: '2.25rem',
  display: 'flex',
  alignItems: 'center',
  fontSize: '1rem',
  fontWeight: '500',
  marginTop: '0.5rem',
  marginBottom: '0.45rem',
  color: '#505050',
  '& .MuiInputLabel-asterisk': {
    color: 'var(--red)',
  },
});

// Tooltip Styled 작업 중
const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} disableHoverListener />
))(({ theme, placement }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#3f3f3f',
    left: placement === 'top-start' ? '1.5rem !important' : '0px !important',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#3f3f3f',
    fontSize: '1rem',
    maxWidth: 'unset',
  },
  inset: placement === 'top-start' ? 'auto auto 0 -1.7rem!important' : 'unset',
}));

const StyledButtonWrap = styled(Box)({
  width: '100%',
  height: '100%',
  ...d_flex,
});

const StyledButton = styled(Box)(props => ({
  width: '3rem',
  height: '2.25rem',
  fontSize: '0.875rem',
  border: `1px solid ${props.theme.palette.gray_1.main}`,
  cursor: 'pointer',
}));

function InputTitle(props: InputTitleProps) {
  const location = useLocation();
  const user_info = useSelector((state: RootState) => state.UserReducer);

  const title = props.title;
  const tooltip = props.tooltip;
  const required = props.required;
  const visibleButton = props.visibleButton;
  const classification = props.classification;
  const onClickButtonName = props.onClickButtonName;
  const onClickButtonTime = props.onClickButtonTime;
  const sx = props.sx;
  const bold = props.bold;
  const open_info = props.openInfo;
  const sms_info = props.smsInfo;
  const icon = props.icon;
  let gray_info = '';

  const [open, setOpen] = useState(false);

  if (title === '청구일') {
    const billData = useRecoilValue<ModalModifyBillDataType>(modalModifyBillData);
    if (billData.visible && billData?.status == '납부만료') {
      gray_info = '청구일을 선택하세요.';
    }
  }

  useEffect(() => {
    if (open_info) {
      setOpen(open_info.open);
    }
  }, [open_info]);

  const isStduentEdit = useMemo(() => {
    return location.pathname.includes('/student/list/edit');
  }, [location]);

  return (
    <StyledInputLabel shrink={false} sx={{ ...sx, fontWeight: bold ? 'bold' : 'initial' }}>
      <Box component={'span'} sx={isStduentEdit && required ? { color: '#3579c5' } : undefined}>
        {title}
        {title === '수업 시작 시간' && (
          <span style={{ color: '#9f9f9f' }}>&nbsp;(결석 메시지를 자동 발송할 경우에만 선택하세요.)</span>
        )}
      </Box>
      {required ? (
        <Typography component='span' sx={{ color: isStduentEdit ? '#3579c5' : '#e9121d' }}>
          &nbsp;*
        </Typography>
      ) : null}
      {tooltip ? (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box component={'span'} sx={{ ml: '0.25rem', cursor: 'pointer' }}>
            <StyledTooltip
              title={tooltip.text}
              placement={tooltip.direction}
              open={open}
              onClose={() => setOpen(false)}
              onClick={() => setOpen(true)}
            >
              <Box component={'span'} sx={{ fontSize: '1.182rem', display: 'flex', alignItems: 'center' }}>
                {icon ? icon : <BsInfoCircle />}
              </Box>
            </StyledTooltip>
          </Box>
        </ClickAwayListener>
      ) : null}
      {sms_info !== '' && sms_info !== undefined && sms_info !== null ? (
        <Typography
          sx={{ fontSize: '1.1rem !important', color: sms_info === '장문' ? 'red !important' : 'black !important' }}
        >
          &nbsp;{sms_info}
        </Typography>
      ) : null}
      {gray_info ? (
        <Box component={'span'} sx={{ color: '#a2a2a2', marginLeft: '0.5rem' }}>
          {gray_info}
        </Box>
      ) : null}
      {visibleButton ? (
        <StyledButtonWrap>
          <StyledButton
            component={'button'}
            sx={{ ml: 'auto' }}
            onClick={onClickButtonName && classification ? () => onClickButtonName(classification) : undefined}
          >
            이름
          </StyledButton>
          <StyledButton
            component={'button'}
            sx={{ ml: '0.25rem' }}
            onClick={onClickButtonTime && classification ? () => onClickButtonTime(classification) : undefined}
          >
            시간
          </StyledButton>
        </StyledButtonWrap>
      ) : null}
    </StyledInputLabel>
  );
}

export default InputTitle;
